import classNames from 'classnames';
import React, { MouseEventHandler, KeyboardEvent } from 'react';

import iconAmex from '../../assets/amex@2x.png';
import bancontactLogo from '../../assets/bancontact_logo.png';
import iconCarteBancaire from '../../assets/cartebancaire@2x.png';
import iconDiners from '../../assets/diners@2x.png';
import iconMaestro from '../../assets/maestro@2x.png';
import iconMc from '../../assets/mc@2x.png';
import iconVisa from '../../assets/visa@2x.png';

import { CardType, CreditCardBrands, Nullable } from '@types-internal';
import { handleKeyDown } from '@utils/utils';

import styles from './CardIcon.module.scss';

export interface CardIconProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  type: Nullable<CardType | CreditCardBrands>;
  className?: string;
  onClick?: MouseEventHandler<HTMLImageElement>;
  /* Indicate that the icon is clickable. If this is true, the cursor will become pointer, otherwise default. */
  clickable?: boolean;
  /* If the icon is "subtle", it will be grayed-out and has an opacity. Used to indicate that this icon
   * is not selected, in contrast with the selected one. */
  subtle?: boolean;
}

const IMAGES: Record<CardType | CreditCardBrands, string | undefined> = {
  visa: iconVisa,
  mastercard: iconMc,
  'diners-club': iconDiners,
  'american-express': iconAmex,
  maestro: iconMaestro,
  discover: undefined,
  jcb: undefined,
  unionpay: undefined,
  elo: undefined,
  mir: undefined,
  hiper: undefined,
  hipercard: undefined,
  cartebancaire: iconCarteBancaire,
  bcmc: bancontactLogo,
  MASTER_CARD: iconMc,
  VISA: iconVisa,
  DINERS: iconDiners,
  DINERS_CLUB: iconDiners,
  AMERICAN_EXPRESS: iconAmex,
  MAESTRO: iconMaestro,
  CARTE_BANCAIRE: iconCarteBancaire,
  BCMC: bancontactLogo,
  OTHER: undefined,
};

const CardIcon: React.FC<CardIconProps> = ({
  className,
  clickable = false,
  onClick,
  type,
  subtle = false,
  ...rest
}) => {
  const image = type ? IMAGES[type] : null;
  const alt = type ?? '';

  if (!image) {
    return null;
  }

  // Only add interactive props if clickable
  const interactiveProps: Partial<React.ImgHTMLAttributes<HTMLImageElement>> = {};
  if (clickable) {
    interactiveProps.onClick = onClick;
    interactiveProps.onKeyDown = (e: KeyboardEvent<HTMLImageElement>) => handleKeyDown(e, onClick);
    interactiveProps.tabIndex = 0;
    interactiveProps.role = 'button';
    interactiveProps['aria-pressed'] = false;
  }

  return (
    <img
      alt={alt}
      className={classNames(
        styles.cardIcon,
        { [styles.clickable]: clickable },
        { [styles.subtle]: subtle },
        className,
      )}
      data-zta={`${type}-icon`}
      src={image}
      {...interactiveProps}
      {...rest}
    />
  );
};

export default CardIcon;
